import React, { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { POSITIONS, DEPARTMENTS } from "@/lib/teamCatalog";

// Shared "raise hiring request" dialog — used from the Hiring Requests page AND from the Team page.
export default function RaiseHiringRequestDialog({ open, onClose, onSaved, defaultDepartment }) {
  const [form, setForm] = useState({
    department: defaultDepartment || "content",
    position: "Writer",
    reason: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.position || !form.department) { toast.error("Position and department are required"); return; }
    setBusy(true);
    try {
      await api.post("/hiring-requests", form);
      toast.success("Hiring request sent to HR");
      onSaved && (await onSaved());
      onClose();
      // reset to defaults for next open
      setForm({ department: defaultDepartment || "content", position: "Writer", reason: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="raise-hiring-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Raise a hiring request</AlertDialogTitle>
          <AlertDialogDescription>The request will be routed to the HR team under this department.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Department</label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger className="mt-1" data-testid="raise-dept"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Profile (position)</label>
              <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                <SelectTrigger className="mt-1" data-testid="raise-position"><SelectValue /></SelectTrigger>
                <SelectContent>{POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium">Notes (optional)</label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why is this needed?" className="mt-1" data-testid="raise-reason" />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="raise-submit">
            {busy ? "Sending…" : "Request"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
